import { AnimatePresence, motion, Variants } from "framer-motion";
import { useRef, useState } from "react";
import styled from "styled-components";
import { fill } from "../styles/utils";
import { CursorHolder } from "./CursorHolder";
import { DefaultCursor } from "./cursors/DefaultCursor";
import Icon from "./Icon";
import { Portal } from "./Portal";

const ZOOM_FACTOR = 3;

interface ImagePannerProps {
  src: string;
  title?: string;
}

const Boundary = styled.div`
  ${fill()}
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto;
  flex-direction: column;
`;

const Container = styled(motion.div)`
  position: relative;
  max-height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  max-width: 100%;
`;

const MotionDragContainer = styled(motion.div)`
  ${fill()}
  cursor: none;
`;

const MotionZoomContainer = styled(motion.div)`
  ${fill()}
  cursor: none;
`;

const HiddenImage = styled.img`
  position: relative;
  max-height: 100%;
  width: auto;
  max-width: 100%;
  opacity: 0;
  pointer-events: none;
  visibility: hidden;
`;

const Image = styled.img`
  ${fill()}
  cursor: none;
  pointer-events: none;
`;

const test: Variants = {
  initial: { scale: 1, x: 0, y: 0 },
  zoomed: ({ x, y }) => ({ x, y, scale: ZOOM_FACTOR }),
};

export const ImagePanner: React.FC<ImagePannerProps> = ({ src, title }) => {
  const [zoomed, setZoomed] = useState(false);
  const [showCursor, setShowCursor] = useState(false);
  const [zoomFactor, setZoomFactor] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });
  const [bound, setBound] = useState<{
    top: number;
    right: number;
    bottom: number;
    left: number;
  }>({
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  });

  const hiddenImageRef = useRef<HTMLImageElement>(null);

  const handleContainerTap = (e: PointerEvent) => {
    const target = e.target as HTMLElement;
    const targetBCR = target.getBoundingClientRect();

    // get max possible movement
    const xMax = Math.abs(
      (window.innerWidth - ZOOM_FACTOR * targetBCR.width) / 2
    );
    const yMax = Math.abs(
      (window.innerHeight - ZOOM_FACTOR * targetBCR.height) / 2
    );

    // get centre of image
    const xCenter = targetBCR.left + targetBCR.width / 2;
    const yCenter = targetBCR.top + targetBCR.height / 2;

    // get factored movement
    const xMovF = -1 * (e.x - xCenter) * ZOOM_FACTOR;
    const yMovF = -1 * (e.y - yCenter) * ZOOM_FACTOR;

    const xMaxM = e.x > xCenter ? -xMax : xMax;
    const yMaxM = e.y > yCenter ? -yMax : yMax;

    const xMov = Math.abs(xMovF) >= xMax ? xMaxM : xMovF;
    const yMov = Math.abs(yMovF) >= yMax ? yMaxM : yMovF;

    setZoomFactor({ x: xMov, y: yMov });
    setZoomed(!zoomed);

    const xIMin = xMax + xMov;
    const xIMax = xMax - xMov;

    const yIMin = yMax + yMov;
    const yIMax = yMax - yMov;
    setBound({ top: -yIMin, right: xIMax, bottom: yIMax, left: -xIMin });
  };

  return (
    <Boundary id="boundary">
      <AnimatePresence>
        {showCursor && (
          <Portal>
            <CursorHolder>
              <DefaultCursor>
                {!zoomed ? <Icon icon="zoomIn" /> : <Icon icon="zoomOut" />}
              </DefaultCursor>
            </CursorHolder>
          </Portal>
        )}
      </AnimatePresence>
      <Container>
        <HiddenImage ref={hiddenImageRef} src={src} />

        <MotionDragContainer
          drag={zoomed}
          dragSnapToOrigin={false}
          dragConstraints={bound}
          animate={zoomed ? { x: 0, y: 0 } : {}}
        >
          <MotionZoomContainer
            onMouseEnter={() => setShowCursor(true)}
            onMouseLeave={() => setShowCursor(false)}
            onTap={handleContainerTap}
            custom={zoomFactor}
            animate={zoomed ? "zoomed" : "initial"}
            variants={test}
            transition={{ type: "tween" }}
          >
            <Image src={src} alt={title}></Image>
          </MotionZoomContainer>
        </MotionDragContainer>
      </Container>
    </Boundary>
  );
};
